import styled from '@emotion/styled'
import { ApiReinvestmentConfirmation } from 'api/_legacy/generated-api'
import { Accordion } from 'components/core-v2/molecules/accordion'
import { useState } from 'react'
import { formatToDateDisplay } from 'utilities/date'

import {
  ExDateDistribution,
  ExDateReinvestment,
  ExpectedReinvestmentData,
  MessageBox,
  Space,
  Typography
} from '../../../components/core-v2'
import Icon from '../../../components/core-v2/atoms/Icon'
import IconLabel from '../../../components/core-v2/molecules/iconLabel'
import PillLabelsStack from '../../../components/core-v2/molecules/pill-labels-stack'
import FundsFinSwitchReinvestment from '../../../components/core-v2/organisms/funds-finswitch-reinvestment'
import { Errors } from '../../tradebuilder-v2/util'
import { ExDateReinvestmentMapping } from '../state/funds-slice'
import { ExDateSchedule, ExDateStatus } from '../types/exDateSchedule'
import { getExDateAlerts } from '../types/mapper'
import FundExDatePreviousForm from './fund-exdate-previous-form'
import FundExDateUpcomingForm from './fund-exdate-upcoming-form'

export type FundExDatesViewProps = {
  arePreviousDates: boolean
  assetId: string
  dates: ExDateSchedule[]
  errors: Errors
  expectedReinvestmentData: ExpectedReinvestmentData
  fetchErrors?: Errors
  onDistributionSubmit?: (updatedDistribution: ExDateDistribution) => void
  onReinvestmentAssign?: (reinvestmentMapping: ExDateReinvestmentMapping) => void
  onReinvestmentSubmit?: (updatedReinvestment: ExDateReinvestment) => void
  onSelection: (exDate: string) => void
  selectedDate: string
  unmappedReinvestments: ApiReinvestmentConfirmation[]
}

export const ExDateViewContainer = styled(Space)`
  background: var(--White);
`

const FundExDatesView = ({
  arePreviousDates,
  assetId,
  dates,
  errors,
  expectedReinvestmentData,
  fetchErrors,
  onDistributionSubmit,
  onReinvestmentAssign,
  onReinvestmentSubmit,
  onSelection,
  selectedDate,
  unmappedReinvestments
}: FundExDatesViewProps) => {
  const [mappedExDate, setMappedExDate] = useState<string>('')
  const renderUserSelected = () => (
    <IconLabel
      icon={<Icon name="toggleUp" size="small" />}
      iconPosition="right"
      text="User selected"
    />
  )
  const renderFinSwitchSelected = () => (
    <IconLabel
      icon={<Icon name="toggleDown" size="small" />}
      iconPosition="right"
      text="FinSwitch selected "
    />
  )

  const renderExDateAlerts = (exDate: ExDateSchedule) => (
    <PillLabelsStack labels={getExDateAlerts(exDate)} px={3} />
  )

  const onRenderSummary = (exDate: ExDateSchedule) => (
    <Space isflex alignItems="center" flexDirection="row" justifyContent="space-between">
      <Space isflex alignItems="center" flexDirection="row" justifyContent="flex-start">
        <Typography variant="b3">{`Ex-Date: ${formatToDateDisplay(exDate.dateStr)}`}</Typography>
        {!arePreviousDates && renderExDateAlerts(exDate)}
      </Space>
      {exDate.providerType === 'Absa' && renderUserSelected()}
      {exDate.providerType !== 'Absa' && renderFinSwitchSelected()}
    </Space>
  )

  const onRenderNoExDatesBanner = () => (
    <MessageBox state="disabled"> No Ex-Dates available for selected fund</MessageBox>
  )

  const onRenderPreviousDate = (exDate: ExDateSchedule) => (
    <FundExDatePreviousForm exDate={exDate} expectedReinvestmentData={expectedReinvestmentData} />
  )

  const onRenderUpcomingDate = (exDate: ExDateSchedule) => (
    <FundExDateUpcomingForm
      errors={errors}
      exDate={exDate}
      expectedReinvestmentData={expectedReinvestmentData}
      onDistributionSubmit={onDistributionSubmit}
      onReinvestmentSubmit={onReinvestmentSubmit}
    />
  )

  const onSelectionToggle = (isExpanded: boolean, exDate: string) => {
    if (isExpanded) {
      onSelection(exDate)
    } else {
      onSelection('')
    }
  }

  const onReinvestmentMappedDateSelection = (confirmationId: string, selectedDate: string) => {
    setMappedExDate(selectedDate)
    onSelectionToggle(true, selectedDate)
    if (onReinvestmentAssign) {
      onReinvestmentAssign({ confirmationId, exDate: selectedDate })
    }
  }

  const onRenderFinswitchReinvestmentsBanner = (
    unmappedReinvestments: ApiReinvestmentConfirmation[]
  ) => {
    const unsubmittedExDates = dates
      .filter(
        item =>
          item.distributionStatus === ExDateStatus.UserSubmitted &&
          item.reinvestmentStatus === ExDateStatus.Waiting
      )
      ?.map(item => item.dateStr)
    return (
      <div>
        {unmappedReinvestments.map(item => (
          <Space key={item.confirmationId} py={5}>
            <FundsFinSwitchReinvestment
              confirmationId={item.confirmationId}
              mappedExDate={mappedExDate}
              reinvestmentDate={item.reinvestmentDate}
              units={item.shares}
              upcomingDates={unsubmittedExDates}
              onDateSelection={date => onReinvestmentMappedDateSelection(item.confirmationId, date)}
            />
          </Space>
        ))}
      </div>
    )
  }

  const onFetchErrors = () => {
    if (fetchErrors && Object.keys(fetchErrors).length > 0) {
      return (
        <Space m={3}>
          <MessageBox state="error">
            {Object.entries(fetchErrors).map(([key, value]) => (
              <div key={key}>
                <Typography variant="b3">{`${value}`}</Typography>
              </div>
            ))}
          </MessageBox>
        </Space>
      )
    }
    return null
  }

  return (
    <Space m={3}>
      {unmappedReinvestments &&
        unmappedReinvestments.length > 0 &&
        onRenderFinswitchReinvestmentsBanner(unmappedReinvestments)}
      {dates && dates.length > 0 ? (
        <ul key="exDates_list_inner">
          {dates.map(exDate => (
            <ExDateViewContainer key={`${assetId}-${exDate.dateStr}`} mb={2}>
              <Accordion
                isExpanded={exDate.dateStr === selectedDate}
                summary={onRenderSummary(exDate)}
                onToggle={isExpanded => onSelectionToggle(isExpanded, exDate.dateStr)}
              >
                {onFetchErrors() ||
                  (arePreviousDates && exDate && onRenderPreviousDate(exDate)) ||
                  (!arePreviousDates && exDate && onRenderUpcomingDate(exDate))}
              </Accordion>
            </ExDateViewContainer>
          ))}
        </ul>
      ) : (
        onRenderNoExDatesBanner()
      )}
    </Space>
  )
}

export default FundExDatesView
